import Chart from 'react-apexcharts';



import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { userAPI } from '../UserAPI';
import { useAtom } from 'jotai';
import { authenticationAtom } from '../State';
import { useCustomerTheme } from '../Theme';
import ApexCharts from 'apexcharts';
import { configuration } from '../Configuration';

import { BarChart3, Download } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ResourceDisclosureHeader, ResourceDisclosurePanel } from './ResourceDisclosure';
// TypeScript declarations for global window properties
// @ts-ignore
window.imageUploadTracker = window.imageUploadTracker || null;

// Global state for expanded resources to persist across re-renders (kept for now as fallback)
const expandedResources = new Map();

/**
 * @typedef {Object} ChartResourceProps
 * @property {any} resource
 * @property {function} [resourceSelected]
 * @property {any} [chartResource]
 * @property {any} [authentication]
 * @property {any} [userAPI]
 * @property {boolean} [isExpanded]
 * @property {function} [onToggleExpanded]
 * @property {string} [stableKey]
 */

/**
 * @param {ChartResourceProps} props
 */
// Define the component props interface
const ChartResourceComponent = ({ 
    resource, 
    resourceSelected: _resourceSelected = undefined,
    chartResource: propChartResource = null,
    authentication: propAuthentication = null,
    userAPI: propUserAPI = null,
    isExpanded: propIsExpanded = undefined,
    onToggleExpanded: propOnToggleExpanded = null,
    stableKey = null
}, ref = null) => {
    const { theme } = useCustomerTheme();
    const [authentication, _setAuthentication] = useAtom(authenticationAtom);
    const resourceKey = stableKey || resource?.reference || resource?._id;
    
    // Use ref to persist expanded state across re-renders
    const expandedRef = useRef(expandedResources.get(resourceKey) || false);
    const [expanded, setExpanded] = useState(propIsExpanded !== undefined ? propIsExpanded : expandedRef.current);
    
    const [chartResource, setChartResource] = useState(propChartResource || null);
    // Use the resource _id directly as the chart ID - 1:1 relationship
    // For shadow components, use the passed chartId, otherwise generate one
    const [chartId] = useState(() => resource._shadowChartId || resourceKey || `chart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    const [imageGenerated, setImageGenerated] = useState(false);
    const [colors] = useState([
        theme.secondary,
        theme.tertiary,
        theme.primary,
        theme.secondaryLight,
        theme.tertiaryLight,
        theme.primaryLight,
    ]);
    const chartRef = useRef(null);
    const shadowResourceCreated = useRef(false);
    const shadowStartTime = useRef(null);
    
    // Use props if provided, otherwise use state
    const currentChartResource = propChartResource || chartResource;
    const currentAuthentication = propAuthentication || authentication;
    const currentUserAPI = propUserAPI || userAPI;
    const currentIsExpanded = propIsExpanded !== undefined ? propIsExpanded : expanded;
    const currentOnToggleExpanded = propOnToggleExpanded || ((e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const newExpanded = !expanded;
        console.log('🖱️ ChartResource toggle, key:', resourceKey, 'new expanded:', newExpanded);
        setExpanded(newExpanded);
        // Update global state immediately
        expandedResources.set(resourceKey, newExpanded);
        expandedRef.current = newExpanded;
    });
    
    // Debug render (commented out for production)
    // console.log('🔍 ChartResource render:', {
    //     resourceKey,
    //     expanded,
    //     currentIsExpanded,
    //     propIsExpanded,
    //     timestamp: Date.now()
    // });
    
    // Expose ref methods (always call useImperativeHandle to follow Rules of Hooks)
    React.useImperativeHandle(ref, () => ({
        getChart: () => {
            // Try to find the ApexCharts instance from the DOM element
            if (chartRef.current) {
                // Look for ApexCharts instance in the DOM element
                const apexChart = chartRef.current.querySelector('.apexcharts-canvas')?.__apexcharts__;
                if (apexChart) {
                    return apexChart;
                }
                // Alternative: look for ApexCharts instance in the element's data
                const chartInstance = chartRef.current.__apexcharts__;
                if (chartInstance) {
                    return chartInstance;
                }
            }
            return null;
        }
    }), []);
    
    // All useEffect hooks must be called before any early returns
    useEffect(() => {
        const call = async () => {
            let newResource = null;

            if (resource.reference) {
                newResource = await userAPI.getResource(authentication.customer, resource.reference);
            } else {
                newResource = resource;
            }

            if (newResource) {
                setChartResource(newResource);
            }
        };

        // Only call if we have a reference or if we don't have local data yet
        if (resource.reference || !chartResource) {
            call();
        }
    }, [resource.reference, resource._id, authentication.customer]);

    // Create shadow resource for streaming image generation
    useEffect(() => {
        if (currentChartResource && currentChartResource.chart && !imageGenerated && resource._streamingContext && !shadowResourceCreated.current) {
            // Double-check DOM to prevent multiple shadow resources
            const existingShadow = document.getElementById(`shadow-chart-${currentChartResource._id}`);
            if (existingShadow) {
                return;
            }
            
            // Additional check: prevent shadow creation if resource already has an image
            if (currentChartResource.imageGenerated || currentChartResource.hasImage) {
                setImageGenerated(true);
                return;
            }
            
            shadowResourceCreated.current = true;
            shadowStartTime.current = Date.now();
            console.log(`📊 Chart shadow rendering started: ${currentChartResource._id}`);
            createShadowChartResource();
        }
    }, [currentChartResource?._id, resource._streamingContext]);
    
    // Create shadow chart resource for streaming image generation
    const createShadowChartResource = () => {
        if (imageGenerated) return;
        
        // Prevent multiple shadow resources for the same resource
        const existingShadow = document.getElementById(`shadow-chart-${currentChartResource._id}`);
        if (existingShadow) return;
        
        // Create shadow container
        const shadowContainer = document.createElement('div');
        shadowContainer.id = `shadow-chart-${currentChartResource._id}`;
        shadowContainer.style.position = 'absolute';
        shadowContainer.style.left = '-9999px';
        shadowContainer.style.top = '-9999px';
        shadowContainer.style.width = '600px';
        shadowContainer.style.height = '350px';
        shadowContainer.style.visibility = 'visible'; // Make visible for chart rendering
        shadowContainer.style.opacity = '1'; // Make fully visible for chart rendering
        shadowContainer.style.zIndex = '-1';
        document.body.appendChild(shadowContainer);
        
        // Create shadow ChartResource component (full component, not just ApexCharts)
        const tempChartRef = React.createRef();
        const root = ReactDOM.createRoot(shadowContainer);
        root.render(
            <BrowserRouter>
                <ChartResource
                    ref={tempChartRef}
                    resource={{...resource, _shadowChartId: chartId, _streamingContext: true}} // Pass the chartId and streaming context to shadow component
                    chartResource={currentChartResource}
                    authentication={currentAuthentication}
                    userAPI={currentUserAPI}
                    isExpanded={true} // Force expanded for shadow
                    onToggleExpanded={() => {}} // No-op for shadow
                />
            </BrowserRouter>
        );
        
        // Shadow chart created - events will be handled via chart options
        
        // Hide the shadow container after chart has time to render
        setTimeout(() => {
            shadowContainer.style.opacity = '0';
        }, 2000);
                
        // Add timeout fallback for chart events
        setTimeout(() => {
            if (!imageGenerated) {
                const elapsedTime = shadowStartTime.current ? Date.now() - shadowStartTime.current : 0;
                console.log(`📊 Chart image submitted: ${currentChartResource._id} -> ${elapsedTime}ms`);
                generateAndStoreImage();
                setImageGenerated(true);
                // Purge shadow resource after upload
                setTimeout(() => {
                    const shadowContainer = document.getElementById(`shadow-chart-${currentChartResource._id}`);
                    if (shadowContainer && shadowContainer.parentNode) {
                        shadowContainer.parentNode.removeChild(shadowContainer);
                    }
                }, 1000);
            }
        }, 5000);
    };
    
    // Generate and store chart image in background with simple retry logic
    const generateAndStoreImage = async (retryCount = 0) => {
        if (!chartResource || !chartResource._id || imageGenerated) {
            return;
        }

        // Check if image already exists for this resource
        if (chartResource.imageGenerated || chartResource.hasImage) {
            setImageGenerated(true);
            return;
        }

        // Additional wait time for chart to be fully registered with ApexCharts
        if (retryCount > 0) {
            const waitTime = Math.min(retryCount * 500, 1500); // Reduced: 0.5s, 1s, 1.5s max for retries
            await new Promise(resolve => setTimeout(resolve, waitTime));
        }

        try {
            // Get the chart instance with improved detection
            let chartInstance = ApexCharts.getChartByID(chartId);
            
            if (!chartInstance) {
                // Wait a bit more for chart to be ready
                await new Promise(resolve => setTimeout(resolve, 500));
                chartInstance = ApexCharts.getChartByID(chartId);
                
                if (!chartInstance) {
                    // Look for chart with matching resource ID in the DOM
                    const chartElements = document.querySelectorAll('[id*="' + chartResource._id + '"]');
                    if (chartElements.length > 0) {
                        const elementId = chartElements[0].id;
                        chartInstance = ApexCharts.getChartByID(elementId);
                    }
                }
            }
            
            if (!chartInstance) {
                throw new Error(`Chart instance not found for ID: ${chartId}`);
            }
            
            // Check if chart container exists and is valid
            if (!chartInstance.w || !chartInstance.w.globals || !chartInstance.w.globals.dom || !chartInstance.w.globals.dom.baseEl) {
                throw new Error('Chart container element not found');
            }
            
            const chartContainer = chartInstance.w.globals.dom.baseEl;
            if (!chartContainer || !chartContainer.style) {
                throw new Error('Chart container is null or has no style property');
            }
            
            // Temporarily make chart visible for image generation
            const originalDisplay = chartContainer.style.display;
            const originalVisibility = chartContainer.style.visibility;
            const originalPosition = chartContainer.style.position;
            const originalLeft = chartContainer.style.left;
            const originalTop = chartContainer.style.top;
            
            // Make chart visible temporarily for image generation
            chartContainer.style.display = 'block';
            chartContainer.style.visibility = 'visible';
            chartContainer.style.position = 'static';
            chartContainer.style.left = 'auto';
            chartContainer.style.top = 'auto';
            
            // Wait for chart to render
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Check if chart is properly rendered
            if (!chartInstance.w.globals.dom.baseEl || chartInstance.w.globals.dom.baseEl.offsetWidth === 0) {
                throw new Error('Chart is not properly rendered');
            }
            
            // Generate the image
            const base64 = await chartInstance.dataURI();
            
            // Restore original visibility and position
            chartContainer.style.display = originalDisplay;
            chartContainer.style.visibility = originalVisibility;
            chartContainer.style.position = originalPosition;
            chartContainer.style.left = originalLeft;
            chartContainer.style.top = originalTop;
            
            // Store image on server with retry logic
            let uploadSuccess = false;
            let uploadAttempts = 0;
            const maxUploadAttempts = 2;
            
            while (!uploadSuccess && uploadAttempts < maxUploadAttempts) {
                uploadAttempts++;
                
                try {
                    const storeResult = await userAPI.storeResourceImage(authentication.customer, 'chart', chartResource._id, base64.imgURI);
                    
                    if (storeResult && storeResult.success) {
                        // Use production API URL with correct /documents/ endpoint format
                        const finalUrl = `${configuration.productionAPIURL}/documents/${authentication.customer}/resource-images/chart/${chartResource._id}.png`;
                        
            const elapsedTime = shadowStartTime.current ? Date.now() - shadowStartTime.current : 0;
            console.log(`✅ Chart upload successful: ${chartResource._id} -> ${finalUrl} (${elapsedTime}ms)`);
            
            // Update status to completed
            if (window.imageUploadTracker) {
                window.imageUploadTracker.updateStatus(chartResource._id, 'completed');
            }
                        
                        uploadSuccess = true;
                    } else {
                        throw new Error(storeResult?.error || 'Upload failed');
                    }
                } catch (uploadError) {
                    if (uploadAttempts < maxUploadAttempts) {
                        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
                    }
                }
            }
            
            if (!uploadSuccess) {
                console.error(`❌ Chart upload failed after 3 retries: ${chartResource._id}`);
                
                // Update status to failed
                if (window.imageUploadTracker) {
                    window.imageUploadTracker.updateStatus(chartResource._id, 'failed');
                }
            }
            
            setImageGenerated(true);
            
        } catch (error) {
            // Retry logic for generation
            if (retryCount < 2) {
                setTimeout(() => {
                    generateAndStoreImage(retryCount + 1);
                }, 1000); // Reduced from 2000ms to 1000ms for faster completion
            } else {
                console.error(`❌ Chart upload failed after 3 retries: ${chartResource._id}`);
                
                // Update status to failed
                if (window.imageUploadTracker) {
                    window.imageUploadTracker.updateStatus(chartResource._id, 'failed');
                }
                setImageGenerated(true);
            }
        }
    };

    // Download chart as PNG
    const downloadPNG = async () => {
        const chartInstance = ApexCharts.getChartByID(chartId);
        
        if (!chartInstance) {
            return;
        }
        
        try {
            // Temporarily make chart visible for download
            const chartContainer = chartInstance.w.globals.dom.baseEl;
            const originalDisplay = chartContainer.style.display;
            const originalVisibility = chartContainer.style.visibility;
            const originalPosition = chartContainer.style.position;
            const originalLeft = chartContainer.style.left;
            const originalTop = chartContainer.style.top;
            
            // Make chart visible temporarily
            chartContainer.style.display = 'block';
            chartContainer.style.visibility = 'visible';
            chartContainer.style.position = 'static';
            chartContainer.style.left = 'auto';
            chartContainer.style.top = 'auto';
            
            // Wait a bit for the chart to render
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Generate the image
            const base64 = await chartInstance.dataURI();
            
            // Restore original visibility and position
            chartContainer.style.display = originalDisplay;
            chartContainer.style.visibility = originalVisibility;
            chartContainer.style.position = originalPosition;
            chartContainer.style.left = originalLeft;
            chartContainer.style.top = originalTop;
            
            // Create download link
            const downloadLink = document.createElement("a");
            downloadLink.href = base64.imgURI;
            downloadLink.download = `${chartResource?.title || 'chart'}.png`;
            
            // Add the anchor element to the document
            document.body.appendChild(downloadLink);
            
            // Simulate a click event to initiate the download
            downloadLink.click();
            
            // Remove the anchor element from the document
            document.body.removeChild(downloadLink);
        } catch (error) {
            console.error('Error downloading chart:', error);
        }
    };
    
    // Apply color scheme



    // Render chart function
    const renderChart = () => {
        if (resource._streamingContext) {
            console.log('📊 Shadow chart renderChart called for:', chartId);
        }
        if (!chartResource?.chart?.options || !Array.isArray(chartResource?.chart?.series) || !chartResource?.chart?.type) {
            if (resource._streamingContext) {
                console.log('📊 Shadow chart no data available for:', chartId);
            }
            return <div className="textM">No chart data available</div>;
        }

        // Ensure series is properly formatted
        const series = Array.isArray(chartResource.chart.series) ? chartResource.chart.series : [];
        const options = typeof chartResource.chart.options === 'object' ? chartResource.chart.options : {};
        const type = chartResource.chart.type;

        // Debug logging for shadow components
        if (resource._streamingContext) {
            console.log('📊 Adding chart events for shadow component:', chartId);
            // Add a timeout to check if events fire later
            setTimeout(() => {
                console.log('📊 Checking if chart events fired after 3 seconds...');
                // Check if shadow container is still in DOM
                const shadowContainer = document.getElementById(`shadow-chart-${currentChartResource._id}`);
                console.log('📊 Shadow container still exists:', !!shadowContainer);
                if (shadowContainer) {
                    console.log('📊 Shadow container dimensions:', shadowContainer.offsetWidth, 'x', shadowContainer.offsetHeight);
                    console.log('📊 Shadow container visibility:', shadowContainer.style.visibility);
                    console.log('📊 Shadow container opacity:', shadowContainer.style.opacity);
                }
            }, 3000);
        }

        // CRITICAL: Set the chart ID in options so ApexCharts can find the instance
        const chartOptions = {
            ...options,
            colors: options.colors || colors,
            chart: {
                ...options.chart,
                id: chartId,
                // Das Diagramm entsteht in dem Augenblick, in dem die Kachel
                // aufgeht. Liefen dann noch der Einlauf von ApexCharts und
                // dessen Neuzeichnen bei Größenänderung mit, teilten sich drei
                // Bewegungen einen Thread — das Aufklappen ruckte. Die Kachel
                // bringt ihre eigene Bewegung mit; das Diagramm steht sofort.
                // Ein Resource, das es anders will, behält seine Angabe.
                animations: options.chart?.animations ?? { enabled: false },
                // Feste Zeichenfläche (600×350): Ein Neuzeichnen bei jeder
                // Höhenänderung des Rahmens bringt nichts und kostet jeden
                // Frame — es ist auch der Pfad, auf dem ApexCharts über einen
                // bereits entfernten Knoten stolpert.
                redrawOnParentResize: options.chart?.redrawOnParentResize ?? false,
                redrawOnWindowResize: options.chart?.redrawOnWindowResize ?? false,
            },
            // Add events for shadow components (streaming context)
            events: resource._streamingContext ? {
                mounted: (_chartContext, _config) => {
                    // Chart mounted
                },
                        animationEnd: (_chartContext, _options) => {
                            if (!imageGenerated) {
                                const elapsedTime = shadowStartTime.current ? Date.now() - shadowStartTime.current : 0;
                                console.log(`📊 Chart image submitted: ${currentChartResource._id} -> ${elapsedTime}ms`);
                                generateAndStoreImage();
                                setImageGenerated(true);
                                // Purge shadow resource after upload
                                setTimeout(() => {
                                    const shadowContainer = document.getElementById(`shadow-chart-${currentChartResource._id}`);
                                    if (shadowContainer && shadowContainer.parentNode) {
                                        shadowContainer.parentNode.removeChild(shadowContainer);
                                    }
                                }, 1000);
                            }
                        },
                        dataLoaded: (_chartContext, _options) => {
                            if (!imageGenerated) {
                                const elapsedTime = shadowStartTime.current ? Date.now() - shadowStartTime.current : 0;
                                console.log(`📊 Chart image submitted: ${currentChartResource._id} -> ${elapsedTime}ms`);
                                generateAndStoreImage();
                                setImageGenerated(true);
                                // Purge shadow resource after upload
                                setTimeout(() => {
                                    const shadowContainer = document.getElementById(`shadow-chart-${currentChartResource._id}`);
                                    if (shadowContainer && shadowContainer.parentNode) {
                                        shadowContainer.parentNode.removeChild(shadowContainer);
                                    }
                                }, 1000);
                            }
                        }
            } : undefined
        };

        return <div ref={chartRef}>
            <Chart
                options={chartOptions}
                series={series}
                type={type}
                height={350}
                width={600}
                onChartReady={(_chartContext, _config) => {
                    // Chart ready
                    if (resource._streamingContext) {
                        console.log('📊 Shadow chart onChartReady fired for:', chartId);
                    }
                }}
            />
        </div>;
    };

    return <div className="box-border w-full">
        <ResourceDisclosureHeader
            expanded={currentIsExpanded}
            onToggle={(e) => currentOnToggleExpanded(e)}
            testId="chat-resource-chart-toggle"
            icon={<BarChart3 aria-hidden="true" className="h-5 w-5 shrink-0" />}
            title={currentChartResource?.title}
            actions={currentChartResource && (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="ml-2 h-8 w-8 shrink-0"
                    onClick={downloadPNG}
                    title="Download chart as PNG"
                    aria-label="Download chart as PNG"
                    data-testid="chat-resource-chart-download"
                >
                    <Download aria-hidden="true" className="h-4 w-4" />
                </Button>
            )}
        />

        <ResourceDisclosurePanel expanded={currentIsExpanded && !!currentChartResource}>
            <div className='flex justify-center'>
                <div>
                    {(() => {
                        try {
                            return renderChart();
                        } catch (error) {
                            console.error('Error rendering chart:', error);
                            return <div className="text-sm text-destructive">Error rendering chart. Please try again later.</div>;
                        }
                    })()}
                </div>
            </div>
        </ResourceDisclosurePanel>

    </div>;
};

// Export the component with proper TypeScript support and memoization
export const ChartResource = React.memo(React.forwardRef(ChartResourceComponent), (prevProps, nextProps) => {
    // Custom comparison function for memoization
    return (
        prevProps.stableKey === nextProps.stableKey &&
        prevProps.resource?.reference === nextProps.resource?.reference &&
        prevProps.resource?._id === nextProps.resource?._id &&
        prevProps.isExpanded === nextProps.isExpanded &&
        prevProps.chartResource === nextProps.chartResource &&
        // Also check data and options for changes to ensure the chart updates
        prevProps.resource?.data === nextProps.resource?.data &&
        prevProps.resource?.options === nextProps.resource?.options
    );
});
